import { useMemo, useState } from 'react'
import type { CTA, VariacaoPrecosSection } from '../../types/payload'
import { KpiCard } from '../KpiCard'
import { ChartExportButton } from '../charts/ChartExportButton'
import styles from './Section.module.css'

interface Props {
  section: VariacaoPrecosSection
  ctas: CTA[]
  onCtaClick: (cta: CTA) => void
}

function estimateChipWidth(label: string) {
  return label.length * 6.5 + 12
}

function buildSmoothPath(points: Array<{ x: number; y: number }>) {
  if (points.length === 0) {
    return ''
  }

  if (points.length === 1) {
    return `M ${points[0].x} ${points[0].y}`
  }

  const tension = 0.4
  let path = `M ${points[0].x} ${points[0].y}`

  for (let index = 0; index < points.length - 1; index += 1) {
    const previous = points[index - 1] ?? points[index]
    const current = points[index]
    const next = points[index + 1]
    const following = points[index + 2] ?? next

    const controlPoint1X = current.x + ((next.x - previous.x) / 6) * tension
    const controlPoint1Y = current.y + ((next.y - previous.y) / 6) * tension
    const controlPoint2X = next.x - ((following.x - current.x) / 6) * tension
    const controlPoint2Y = next.y - ((following.y - current.y) / 6) * tension

    path += ` C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${next.x} ${next.y}`
  }

  return path
}

export function VariacaoPrecos({ section, ctas, onCtaClick }: Props) {
  const periodWindowLabel = section.period_window
    ? `${section.period_window.start} a ${section.period_window.end}`
    : 'janela móvel de 12 meses'
  const categories = section.category_order ?? section.historical_series.map(item => item.categoria)
  const [activeCategory, setActiveCategory] = useState(categories[0] ?? '')
  const [viewMode, setViewMode] = useState<'annual' | 'weekly'>(section.default_view ?? 'annual')
  const [hoveredPoint, setHoveredPoint] = useState<{
    x: number
    y: number
    label: string
    value: string
    series: string
  } | null>(null)

  const activeSeries = useMemo(
    () => {
      const sourceSeries = viewMode === 'weekly'
        ? (section.weekly_series?.length ? section.weekly_series : section.historical_series)
        : section.historical_series

      return sourceSeries.find(item => item.categoria === activeCategory) ?? sourceSeries[0]
    },
    [activeCategory, section.historical_series, section.weekly_series, viewMode],
  )

  const activeViewLabel = viewMode === 'weekly' ? '12 últimas semanas' : `Ano móvel de ${periodWindowLabel}`
  const hasWeeklyData = Boolean(section.weekly_series?.some(item => item.series.length > 0))
  const hasActiveSeriesData = Boolean(activeSeries?.series.length)
  const methodologyNotes = [
    viewMode === 'weekly' ? 'Visão últimas 12 semanas.' : 'Visão últimos 12 meses.',
    'Para esse cálculo de variação são considerados apenas produtos que estejam presentes nas bases comparáveis, extraindo o efeito da variação do mix da análise.',
  ]

  const coordinates = useMemo(() => {
    if (!activeSeries || activeSeries.series.length === 0) {
      return null
    }

    const allValues = activeSeries.series.flatMap(point => [point.mp_pct, point.mf_pct])
    const min = Math.min(...allValues)
    const max = Math.max(...allValues)
    const range = max - min || 1

    const toY = (value: number) => 210 - ((value - min) / range) * 170
    const toX = (index: number) => 30 + index * (720 / Math.max(activeSeries.series.length - 1, 1))

    const mp = activeSeries.series.map((point, index) => ({
      x: toX(index),
      y: toY(point.mp_pct),
      label: point.period,
      value: point.mp_pct,
    }))

    const mf = activeSeries.series.map((point, index) => ({
      x: toX(index),
      y: toY(point.mf_pct),
      label: point.period,
      value: point.mf_pct,
    }))

    return { min, max, mp, mf }
  }, [activeSeries])

  const lastSeriesPoint = activeSeries ? activeSeries.series[activeSeries.series.length - 1] : undefined
  const mpPath = coordinates ? buildSmoothPath(coordinates.mp) : ''
  const mfPath = coordinates ? buildSmoothPath(coordinates.mf) : ''

  return (
    <section className={`card ${styles.section}`}>
      <div className={styles.sectionHeader}>
        <div>
          <span className="microlabel">Dashboard de variação por departamento</span>
          <h2 className={styles.sectionTitle}>{activeViewLabel}</h2>
        </div>
        <div className={styles.tabRow} role="tablist" aria-label="Granularidade da série">
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'annual'}
            className={`${styles.tabChip} ${viewMode === 'annual' ? styles.tabChipActive : ''}`}
            onClick={() => setViewMode('annual')}
          >
            Anual
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'weekly'}
            className={`${styles.tabChip} ${viewMode === 'weekly' ? styles.tabChipActive : ''}`}
            onClick={() => setViewMode('weekly')}
            disabled={!hasWeeklyData}
          >
            Semanal
          </button>
        </div>
      </div>

      <div className={styles.tabRow} role="tablist" aria-label="Departamentos">
        {categories.map(category => (
          <button
            key={category}
            type="button"
            role="tab"
            aria-selected={category === activeCategory}
            className={`${styles.tabChip} ${category === activeCategory ? styles.tabChipActive : ''}`}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      {activeSeries && coordinates && (
        <div className={styles.chartCard}>
          <div className={styles.legend}>
            <span className={styles.legendItem}><i className={styles.legendMp} /> Marcas Próprias</span>
            <span className={styles.legendItem}><i className={styles.legendMf} /> Marcas Fabricante</span>
            <span className={styles.legendExportSlot}>
              <ChartExportButton
                getContainer={() =>
                  document.querySelector<HTMLElement>(
                    `[data-chart-container="variacao-precos-${viewMode}-${activeCategory}"]`,
                  )
                }
                filename={`variacao-precos-${viewMode}-${activeCategory}.png`}
                label="Exportar gráfico em PNG"
              />
            </span>
          </div>

          <div
            className={styles.lineChartWrap}
            data-chart-container={`variacao-precos-${viewMode}-${activeCategory}`}
          >
            <svg viewBox="0 0 780 240" className={styles.lineChart} role="img" aria-label={`Variação por categoria ${activeSeries.categoria}`}>
              {[0, 1, 2, 3, 4].map(step => {
                const ratio = step / 4
                const value = coordinates.max - (coordinates.max - coordinates.min) * ratio
                const y = 20 + ratio * 170

                return (
                  <g key={step}>
                    <line x1="30" y1={y} x2="750" y2={y} className={styles.gridLine} />
                    <text x="0" y={y + 4} className={styles.axisText}>
                      {(value * 100).toFixed(0)}%
                    </text>
                  </g>
                )
              })}

              <path
                fill="none"
                stroke="var(--chart-mp)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                d={mpPath}
              />
              <path
                fill="none"
                stroke="var(--chart-mf)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                d={mfPath}
              />

              {coordinates.mp.map((point, index) => (
                <g key={`mp-${point.label}`}>
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="6"
                    fill="var(--chart-mp)"
                    onMouseEnter={() => setHoveredPoint({
                      x: point.x,
                      y: point.y,
                      label: activeSeries.series[index]?.tooltipLabel ?? point.label,
                      value: `${(point.value * 100).toFixed(1)}%`,
                      series: 'Marcas Próprias',
                    })}
                    onFocus={() => setHoveredPoint({
                      x: point.x,
                      y: point.y,
                      label: activeSeries.series[index]?.tooltipLabel ?? point.label,
                      value: `${(point.value * 100).toFixed(1)}%`,
                      series: 'Marcas Próprias',
                    })}
                    onMouseLeave={() => setHoveredPoint(null)}
                  />
                  {(() => {
                    const labelText = `${(point.value * 100).toFixed(1)}%`
                    const chipWidth = estimateChipWidth(labelText)
                    const chipY = point.y - 12 - 11

                    return (
                      <>
                        <rect
                          x={point.x - chipWidth / 2}
                          y={chipY}
                          width={chipWidth}
                          height={15}
                          rx={5}
                          className={styles.pointValueChipMp}
                        />
                        <text
                          x={point.x}
                          y={point.y - 12}
                          textAnchor="middle"
                          className={`${styles.pointValueLabel} ${styles.pointValueMp}`}
                        >
                          {labelText}
                        </text>
                      </>
                    )
                  })()}
                </g>
              ))}

              {coordinates.mf.map((point, index) => (
                <g key={`mf-${point.label}`}>
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="6"
                    fill="var(--chart-mf)"
                    onMouseEnter={() => setHoveredPoint({
                      x: point.x,
                      y: point.y,
                      label: activeSeries.series[index]?.tooltipLabel ?? point.label,
                      value: `${(point.value * 100).toFixed(1)}%`,
                      series: 'Marcas Fabricante',
                    })}
                    onFocus={() => setHoveredPoint({
                      x: point.x,
                      y: point.y,
                      label: activeSeries.series[index]?.tooltipLabel ?? point.label,
                      value: `${(point.value * 100).toFixed(1)}%`,
                      series: 'Marcas Fabricante',
                    })}
                    onMouseLeave={() => setHoveredPoint(null)}
                  />
                  {(() => {
                    const labelText = `${(point.value * 100).toFixed(1)}%`
                    const chipWidth = estimateChipWidth(labelText)
                    const chipY = point.y + 18 - 11

                    return (
                      <>
                        <rect
                          x={point.x - chipWidth / 2}
                          y={chipY}
                          width={chipWidth}
                          height={15}
                          rx={5}
                          className={styles.pointValueChipMf}
                        />
                        <text
                          x={point.x}
                          y={point.y + 18}
                          textAnchor="middle"
                          className={`${styles.pointValueLabel} ${styles.pointValueMf}`}
                        >
                          {labelText}
                        </text>
                      </>
                    )
                  })()}
                </g>
              ))}

              {activeSeries.series.map((point, index) => (
                <text key={point.period} x={30 + index * (720 / Math.max(activeSeries.series.length - 1, 1))} y="232" className={styles.axisText}>
                  {point.period}
                </text>
              ))}
            </svg>

            {hoveredPoint && (
              <div
                className={styles.chartTooltip}
                style={{
                  left: `${(hoveredPoint.x / 780) * 100}%`,
                  top: `${(hoveredPoint.y / 240) * 100}%`,
                }}
              >
                <strong>{hoveredPoint.series}</strong>
                <span>{hoveredPoint.label}</span>
                <span>{hoveredPoint.value}</span>
              </div>
            )}
          </div>

          <div className={styles.inlineHeader}>
            <span className="microlabel">Indicadores da série — {activeSeries.categoria}</span>
            <ChartExportButton
              getContainer={() =>
                document.querySelector<HTMLElement>(`[data-chart-container="variacao-precos-kpis-${viewMode}-${activeCategory}"]`)
              }
              filename={`variacao-precos-kpis-${viewMode}-${activeCategory}.png`}
              label="Exportar gráfico em PNG"
            />
          </div>

          <div
            className={`${styles.metricGrid} ${styles.metricGridCompact}`}
            data-chart-container={`variacao-precos-kpis-${viewMode}-${activeCategory}`}
          >
            <KpiCard
              label="Pico Preço MP"
              value={`${(activeSeries.peak_mp_increase_pct * 100).toFixed(1)}%`}
              meta={activeSeries.peak_mp_increase_period}
              indicator="positive"
              size="dense"
            />
            <KpiCard
              label="Piso Preço Marca Própria"
              value={`${(activeSeries.peak_mp_decrease_pct * 100).toFixed(1)}%`}
              meta={activeSeries.peak_mp_decrease_period}
              indicator="default"
              size="dense"
            />
            <KpiCard
              label={viewMode === 'weekly' ? 'Última Semana MP' : 'Último Mês MP'}
              value={`${((lastSeriesPoint?.mp_pct ?? 0) * 100).toFixed(1)}%`}
              meta={viewMode === 'weekly' ? 'último ponto semanal' : 'último ponto da série'}
              indicator="default"
              size="dense"
            />
          </div>
        </div>
      )}

      {activeSeries && !hasActiveSeriesData && (
        <div className={styles.chartCard}>
          <p className={styles.emptyStateText}>
            A série semanal ainda não foi materializada para este departamento no payload atual.
          </p>
        </div>
      )}

      <ul className={styles.notesList}>
        {methodologyNotes.map(note => <li key={note}>{note}</li>)}
      </ul>

      {ctas.length > 0 && (
        <div className={styles.ctaRow}>
          {ctas.map(cta => (
            <button key={cta.id} type="button" className="btn-cta" onClick={() => onCtaClick(cta)}>
              {cta.label}
            </button>
          ))}
        </div>
      )}
    </section>
  )
}
